import Component from 'vue-class-component';
import { throttle } from 'lodash';
import EdHomeworksCourseContainer from '@/homework/component/ed-homeworks-course-container/ed-homeworks-course-container.vue';
import EdPresentationModeHeader from '@/content/components/ed-presentation-mode/ed-presentation-mode-header/ed-presentation-mode-header.component.vue';
import Course from '@/content/models/course.model';
import { COURSE_EVENTS } from '@/content/values/events';
import WebSocketMessageRequest from '@/content/models/web-socket-message-request.model';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import * as PRESENTATION_VALUES from '@/content/values/ed-presentation-mode.values';
import { TRANSPARENCY_TOOLBAR_HEIGHT } from '@/content/values/ed-presentation-mode.values';
import { Mixins, Prop, Provide, Watch } from 'vue-property-decorator';
import { CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT, FORWARD_PRESENTATION_MESSAGES_EVENT, SHOW_SOCKET_DISCONNECTING_EVENT, _APP_STORE } from '@/core/values/app.values';
import { AUTH_STORE } from '@/auth/store/auth.store';
import { SECTION_TYPES } from '@/content/values/sections';
import { MESSAGE_EVENT, TRANSPARENCY_EVENTS, SECTION_TYPE_CODES } from '@/core/values/ed-transparency-meeting.values';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import EdPresentationModeMixin from '@/content/mixins/ed-presentation-mode.mixin';
import { HOMEWORKS_STORE } from '@/homework/store/homeworks.store';
import { ED_CONTEXT_MENU_EVENTS } from '@/plugins/ed-context-menu/ed-context-menu.values';
import { POPUP_MENU_EVENTS } from '@/values/general.values';
import { Section } from '@/courses/models/section.model';
import BrowserService from '@/providers/browser-utils/browser.service';
import { EdJitsiMixin } from '@/components/screen-share/ed-screen-share/ed-jitsi-mixin'
import {ShareTaskData} from '@/meetings/interfaces/share-task-data.interface';
import {waitForElement} from '@/utils/dom-utils';
import { ITeacherSize } from '@/core/interfaces/presentation-mode-status.interface';

@Component({
  components: {
    EdHomeworksCourseContainer,
    EdPresentationModeHeader
  }
})
export default class EdPresentationModeTeacher extends Mixins(EdPresentationModeMixin, EdJitsiMixin) {

  @Prop() readonly roomId: number;
  @Prop() readonly classId: number;

  @Provide()
  messageEvent = new Map();

  private readonly verticalInitialOffset = TRANSPARENCY_TOOLBAR_HEIGHT;
  private isLoading: boolean = true;
  private scrollValues: any = {};
  private layoutValues: any = {};
  private cardData: any = {};
  private pageSize: ITeacherSize = { teacherWidth: 0, teacherHeight: 0, widthOffset: 0, heightOffset: 0 };
  private isSentPageSize: boolean = false;
  private isSectionLoaded: boolean = false;
  private pendingMessages: any = {};
  private currentSection: any = {};
  private sectionLoaded: any = null;
  private homeworksSideBar: boolean = true;
  private marginLeft: number = 10;
  private marginTop: number = 6;
  private borderLeft: number = 4;
  private whiteboardDocumentInfo: any = {};

  get courses(): Course[] {
    return this.$store.state.CourseStore.courses.content;
  }

  get courseSelected(): number {
    return this.$store.state.CourseStore.presentationCourseSelected;
  }

  get sectionForSelectedCourse() {
    if (!this.courseSelected) {
      return this.currentPresentationSection;
    }
    const courses = this.$store.state.MeetingsStore.currentPresentationSection.perCourse;
    const find = courses.find(entry => entry.courseId === this.courseSelected);
    return find;
  }

  get presentationModeViewerType() {
    return this.$store.state.MeetingsStore.presentationModeViewerType == SECTION_TYPES.SUBDECK.code ? SECTION_TYPE_CODES.LESSON_CARD : this.$store.state.MeetingsStore.presentationModeViewerType;
  }

  get whiteboardPath(): string {
    return this.$store.state.MeetingsStore.transparentBoardPath;
  }

  get isBoardPreviousDisconnectionSocket() : boolean {
    return this.$store.state.AppStore.isBoardPreviousDisconnectionSocket;
  }

  get shareTaskData(): ShareTaskData {
    return this.$store.state.MeetingsStore.shareTaskData;
  }

  get isSplitHorizontal(): boolean {
    return this.$store.state.CourseStore.isSplitHorizontal;
  }

  get isSplitVertical(): boolean {
    return this.$store.state.CourseStore.isSplitVertical;
  }

  private emitConnected() : Promise<void> {
    return new Promise((resolve) => {
      let data = { roomId: this.roomId, device: BrowserService.getFullBrowserInfo() };
      let message: WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequest(PRESENTATION_VALUES.EVENT_TYPES.subscribe,
          String(this.roomId), false, JSON.stringify(data));
      this.$EdSocket.emit(PRESENTATION_VALUES.SOCKET_EVENTS.connect, message);
      setTimeout(() => {
        resolve();
      });
    });
  }

  private processUserEvent(message: WebSocketMessageResponse): void {
    if (!message) {
      return;
    }

    if (message.content && message.messageType === PRESENTATION_VALUES.EVENT_TYPES.render) {
      this.manageRenderMessage(message);
    }

    if (message.username && message.messageType === PRESENTATION_VALUES.EVENT_TYPES.studentConnect) {
      this.$Meetings.Store.addCurrentMeetingActiveUser(message.username);
    }

    if (message.username && message.messageType === PRESENTATION_VALUES.EVENT_TYPES.studentDisconnect) {
      this.$Meetings.Store.removeCurrentMeetingActiveUser(message.username);
    }
  }

  private manageRenderMessage(message : WebSocketMessageResponse) : void {
    let messageData = JSON.parse(message.content);
    let dataToSend = {};

    for(let type of messageData){
        if(type === PRESENTATION_VALUES.EVENT_TYPES.resize){
            dataToSend[PRESENTATION_VALUES.EVENT_TYPES.resize] = this.pageSize;
        }

        if(type === PRESENTATION_VALUES.EVENT_TYPES.task){
            dataToSend[PRESENTATION_VALUES.EVENT_TYPES.task] = this.sectionLoaded;
        }
    }

    let response : WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequest(PRESENTATION_VALUES.EVENT_TYPES.render, String(this.roomId), false, JSON.stringify(dataToSend), message.username);
    this.$EdSocket.emit(PRESENTATION_VALUES.SOCKET_EVENTS.requestInformation, response);
  }

  private loadCourses(): void {
    this.$Courses.Store.getPresentationCoursesA({classId: this.classId})
      .finally(() => {
        this.isLoading = false;
      });
  }

  private async loadPresentationSection(data) : Promise<void> {
    if (!data) {
      return;
    }

    this.layoutValues = {};
    this.isSectionLoaded = true;
    this.sectionLoaded = data;
    this.whiteboardDocumentInfo = {};
    this.$edEventBus.$emit(PRESENTATION_VALUES.CLEAN_CURRENT_SHARED_TASK_EVENT);

    setTimeout(async () => {
      this.currentSection = { ...data };
      this.$Meetings.Store.updateCurrentPresentationSection(this.currentSection);
      this.updatePageSize({ width: this.pageSize.teacherWidth, height: this.pageSize.teacherHeight, homeworksSideBar: this.homeworksSideBar });
      this.sendResizeMessage();
    });
    this.$Meetings.Store.addCurrentPageSizes({});
    this.sendTaskEvent(data);
  }

  private async sendTaskEvent(data: any) : Promise<void> {
    this.pendingMessages = {};
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.task, data);
    await this.sendResetPdfViewerScrollValuesEvent()
  }

  public iterateElementsToCreateEvents() : void {
    this.createScrollEvent(PRESENTATION_VALUES.SCROLL_ELEMENTS.pdf.className, PRESENTATION_VALUES.SCROLL_ELEMENTS.pdf.type, true);
    this.createScrollEvent(PRESENTATION_VALUES.SCROLL_ELEMENTS.images.className, PRESENTATION_VALUES.SCROLL_ELEMENTS.images.type, false);
    // Types based on text have custom cursors
    // this.createScrollEvent(PRESENTATION_VALUES.SCROLL_ELEMENTS.text.className, PRESENTATION_VALUES.SCROLL_ELEMENTS.text.type, false);
    // this.createScrollEvent(PRESENTATION_VALUES.SCROLL_ELEMENTS.responseType.className, PRESENTATION_VALUES.SCROLL_ELEMENTS.responseType.type, false);
    this.createMouseEvent();
  }

  private createScrollEvent(className: string, elementType: string, isClear: boolean): void {
    if (isClear) {
      this.scrollValues = {};
    }
    const elements = document.getElementsByClassName(className);
    for (let index = 0; index < elements.length; index++) {
      const pdfElement = elements[index] as HTMLElement;

      // Create throttled function for sending scroll updates
      const throttledSendScroll = throttle(async () => {
        await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.scroll, this.scrollValues);
      }, PRESENTATION_VALUES.SEND_MESSAGE_SCROLL_TIMER);

      pdfElement.onscroll = (event) => {
        const target = event.target as HTMLElement;
        const scrollHeight = target.scrollHeight - this.verticalInitialOffset;
        const scrollTop = target.scrollTop;
        const scrollPercentage = scrollTop / scrollHeight;

        const data = { scrollTop: scrollPercentage, elementIndex: index, type: elementType };
        this.scrollValues[`${elementType}${index}`] = data;

        // Call the throttled function
        throttledSendScroll();
      };
    }
  }

  private async forceSendScroll() {
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.scroll, this.scrollValues);
  }

  private async createMouseEvent(): Promise<void> {
    let containerInfoElement = await waitForElement('#courseContainerComponent');
    let containerInitialInfoPosition = containerInfoElement.getBoundingClientRect();
    
    // Create throttled function that includes all the calculations and message sending
    const throttledProcessMouseMove = throttle(async (event: MouseEvent) => {
      if (this.presentationModeViewerType === SECTION_TYPES.LESSON_TEXT.code || this.presentationModeViewerType === SECTION_TYPES.LESSON_IMAGES.code) {
        containerInitialInfoPosition = containerInfoElement.getBoundingClientRect();
      }
      
      let pageXPointer = event.pageX;
      if (this.homeworksSideBar || this.presentationModeViewerType === SECTION_TYPES.LESSON_TEXT.code || this.presentationModeViewerType === SECTION_TYPES.LESSON_IMAGES.code) {
        pageXPointer = Math.abs(event.pageX - containerInitialInfoPosition.left) - this.borderLeft;
      }

      let pdfContainer = document.getElementsByClassName('pdf-container');
      if (pdfContainer.length > 0 && !this.homeworksSideBar) {
        let pdfContainerElement: any = pdfContainer[0];
        let newContainer = containerInfoElement.offsetWidth / 2;
        let newPdfContainer = pdfContainerElement.offsetWidth / 2;
        let containerDifference = (newContainer - newPdfContainer) + this.marginLeft;
        pageXPointer -= containerDifference;
      }

      let data = { 
        pageX: pageXPointer, 
        pageY: event.pageY - containerInitialInfoPosition.top - this.marginTop, 
        homeworksSideBar: this.homeworksSideBar 
      };
      await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.cursor, data);
    }, PRESENTATION_VALUES.SEND_MESSAGE_CURSOR_TIMER);
    
    // Attach the event handler
    containerInfoElement.onmousemove = (event) => {
      if (this.isWhiteboardTransparency) {
        // When transparency is enabled, we rely on the wbo cursor mechanism to send the cursor position instead of
        // sending it through realtime.
        return;
      }
      
      // Only execute the throttled function when needed
      throttledProcessMouseMove(event);
    };
  }

  private async sendLayoutEvent(layoutInfo: any) : Promise<void> {
    if (!layoutInfo.elementId || !layoutInfo.type || layoutInfo.elementId === PRESENTATION_VALUES.SOLUTION_TAB_ID) {
      return;
    }

    this.layoutValues[layoutInfo.type] = layoutInfo.elementId;

    if('layout' == layoutInfo.type) {
      this.layoutValues['tab'] = 'ProblemTab';
    }

    this.updatePageSize({ width: this.pageSize.teacherWidth, height: this.pageSize.teacherHeight, homeworksSideBar: this.homeworksSideBar });
    await this.sendResizeMessage();
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.layout, this.layoutValues);
    await this.sendResetPdfViewerScrollValuesEvent();
  }

  private async sendResetPdfViewerScrollValuesEvent() {
    let elementType = PRESENTATION_VALUES.SCROLL_ELEMENTS.pdf.type;
    let index0 = 0, index1 = 1;
    let messageValue: any = {};
    messageValue[`${elementType}${index0}`] = { scrollTop: 0, elementIndex: index0, type: elementType };
    messageValue[`${elementType}${index1}`] = { scrollTop: 0, elementIndex: index1, type: elementType };
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.scroll, messageValue);
    this.scrollValues = {};
  }

  private async changeStateVideo(data: any, onlySave: boolean): Promise<void> {
    let dataSend = JSON.stringify(data);
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.video, dataSend, onlySave);
  }

  private sendMessage(messageType: string, data: any, onlySave: boolean = false): Promise<void> {
    return new Promise(resolve => {
      if (!this.$store.state.AppStore.presentationSocketConnected || !this.isPresentationNavigation) {
        this.pendingMessages[messageType] = { data, onlySave };
        resolve();
        return;
      }

      delete this.pendingMessages[messageType];
      let message: WebSocketMessageRequest = EdPresentationModeFactory.createWebSocketMessageRequest(messageType, String(this.roomId), onlySave, JSON.stringify(data));
      this.$EdSocket.emit(PRESENTATION_VALUES.SOCKET_EVENTS.events, message, () => resolve());
    });
  }

  private validateParamsToInit(): void {
    if (!(this.$store.state.AppStore.presentationSocketConnected && this.userId)) {
      return;
    }
    this.processPendingMessages();
  }

  private processPendingMessages() : void {
    this.sendPendingMessages().then(async () => await this.emitConnected());
  }

  private validateStoreValues() {
    this.$store.subscribe((mutation) => {
      if (mutation.type === _APP_STORE.MUTATIONS.SET_PRESENTATION_SOCKET_CONNECTED || mutation.type === AUTH_STORE.MUTATIONS.SET_USER_INFO) {
        this.validateParamsToInit();
      }
    });
  }

  /**
   * Calculate vertical offset based on section type and layout
   */
  private calculateVerticalOffset(): number {
    const isSplitObject = this.presentationModeViewerType === SECTION_TYPE_CODES.LESSON_SPLIT_WRITE || 
                          this.presentationModeViewerType === SECTION_TYPE_CODES.LESSON_SPLIT_TYPE;
    
    if (isSplitObject) {
      return this.isSplitHorizontal ? this.verticalInitialOffset * 4 : this.verticalInitialOffset * 2;
    }
    
    return this.verticalInitialOffset;
  }

  /**
   * Update pageSize based on width, height and homeworksSideBar
   */
  private updatePageSize({ width, height, homeworksSideBar }: { width: number, height: number, homeworksSideBar: boolean }): void {
    this.homeworksSideBar = homeworksSideBar;
    const verticalOffset = this.calculateVerticalOffset();
    
    this.pageSize = { teacherWidth: width, teacherHeight: height, widthOffset: 0, heightOffset: verticalOffset };
  }

  /**
   * Handle resize message sending logic based on currently selected object
   */
  private async sendResizeMessage(): Promise<void> {
    if (!this.isSectionLoaded) {
      this.validateConnected();
      return;
    }

    if (!this.isSentPageSize) {
      this.isSentPageSize = true;
      await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.resize, this.pageSize);
      this.validateConnected();
      return;
    }

    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.resize, this.pageSize);
  }

  private async changeResize({ width, height, homeworksSideBar }: { width: number, height: number, homeworksSideBar: boolean }) : Promise<void> {
    this.updatePageSize({ width, height, homeworksSideBar });
    await this.sendResizeMessage();
  }

  private async changePdfZoom(value: boolean) : Promise<void> {
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.zoom, { zoom: value });
  }

  private async changeCard({ card , isFrontSide, index }: { card: Section, isFrontSide: boolean, index: number }) : Promise<void> {
    this.cardData = { card , isFrontSide, index };
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.card, this.cardData);
  }

  private async sendTransparentWhiteboard() : Promise<void> {
    if(this.whiteboardDocumentInfo.isTransparency === this.isWhiteboardTransparency){
      return;
    }
    let whiteboardDocumentInfo: any = { isTransparency: this.isWhiteboardTransparency };
    this.whiteboardDocumentInfo = whiteboardDocumentInfo;
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.whiteboard, whiteboardDocumentInfo);
  }

  private transparencyEvents(event: any) {
    if (!this.whiteboardPath.includes(event.origin)){
      return;
    }

    if(event.data.scrollTop !== null && event.data.documentIndex !== undefined) {
      this.$edEventBus.$emit(`${TRANSPARENCY_EVENTS.SCROLL_EVENT}${event.data.documentIndex}`, event.data);
    } else if(event.data.isClick){
      this.$edEventBus.$emit(POPUP_MENU_EVENTS.CLOSE_POPUP_MENU)
      this.$EdContextMenu.$emit(ED_CONTEXT_MENU_EVENTS.CLOSE_CONTEXT_MENU);
      this.$edEventBus.$emit(PRESENTATION_VALUES.CLOSE_NAV_MEETING_TOOLS_EVENT);
      this.$edEventBus.$emit(PRESENTATION_VALUES.CLOSE_MENU_TOOLS_EVENT);
    } else if(event.data.isDisconnected !== undefined){
      this.manageTransparencyDisconnectMessage(event.data.isDisconnected);
    }
  }

  private manageTransparencyDisconnectMessage(isDisconnected : boolean){
    if(isDisconnected){
      this.$edEventBus.$emit(SHOW_SOCKET_DISCONNECTING_EVENT, {isBoard: true});
    } else if(!isDisconnected && this.isBoardPreviousDisconnectionSocket){
      this.$edEventBus.$emit(CLOSE_BOARD_SOCKET_DISCONNECTING_EVENT);
    }
  }

  private async loadPresentationElements(): Promise<void> {
    this.$nextTick(() => {
      this.iterateElementsToCreateEvents();
    });
  }

  private async changeMultimediaState({ data, onlySave, type }: { data: any, onlySave: boolean, type: string }) : Promise<void> {
    let dataSend = JSON.stringify(data);
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES[type], dataSend, onlySave);
  }

  private async sendShareTask(data : any) : Promise<void> {
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.shareTask, data, false);
  }

  private async changeSlideData(data: any) : Promise<void> {
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.changeSlide, data, false);
  }

  private async sendTextChanged(data: any): Promise<void> {
    await this.sendMessage(PRESENTATION_VALUES.EVENT_TYPES.text, data, false);
  }

  private validateConnected(): void {
    if (this.$store.state.AppStore.presentationSocketConnected && this.userId) {
      this.processPendingMessages();
    } else {
      this.validateStoreValues();
    }
  }


  private async sendPendingMessages() : Promise<void> {
    let messagePromises: Promise<void>[] = [];
    return new Promise((resolve) => {
      if(Object.keys(this.pendingMessages).length > 0){
        for(let key of PRESENTATION_VALUES.ORDER_PENDING_MESSAGES ){
          if(this.pendingMessages[key]){
            messagePromises.push(this.sendMessage(key, this.pendingMessages[key].data, this.pendingMessages[key].onlySave));
          }
        }
        Promise.all(messagePromises).then(() => resolve());
      } else {
        resolve();
      }
    });
  }

  private forwardMessages(): void {
    this.restoreTeacherTask();
    let messagePromises: Promise<void>[] = [];
    for (let [event, data] of PRESENTATION_VALUES.PRESENTATION_MODE_RECONNECT_MESSAGES) {
      if (this.validateMessageTypes(data)) {
        messagePromises.push(this.sendMessage(event, this[data.property]));
      }
    }
    Promise.all(messagePromises).then(async () => await this.emitConnected());
  }

  private validateMessageTypes(data: { [key: string]: string }): boolean {
    if (!data || this[data.property] === undefined || this[data.property] === null) {
      return false;
    }
    if (data.type === PRESENTATION_VALUES.PRESENTATION_MODE_RECONNECT_MESSAGES_TYPES.OBJECT
      && Object.keys(this[data.property]).length === 0) {
      return false;
    }
    return true;
  }

  private restoreTeacherTask(): void {
    this.$Meetings.Store.updateCurrentBoardUser({
      isTeacher: true,
      sectionId: this.currentPresentationSection.sectionId,
      fullName: this.$t('myContent.teacher')
    });
    if(this.isWhiteboardTransparency){
      this.$edEventBus.$emit(PRESENTATION_VALUES.SEND_CURRENT_SHARED_TASK_EVENT, false);
    }
  }

  @Watch('courseSelected')
  private async changeCourse() {
    if (this.sectionForSelectedCourse) {
      this.$Meetings.Store.updateCurrentPresentationSection(this.sectionForSelectedCourse);
      await this.sendTaskEvent(this.sectionForSelectedCourse);
      this.$Meetings.Store.setShareTaskData({
        active: true,
        isTeacher: true,
        fullName: this.$t('myContent.teacher'),
        userId: this.userLoginId
      });
      await this.sendShareTask(this.shareTaskData);
    }
    this.restoreTeacherTask();
  }

  mounted() {
    this.loadCourses();
    this.$edEventBus.$on(COURSE_EVENTS.FORCE_SEND_SCROLL, this.forceSendScroll);
    this.$edEventBus.$on(COURSE_EVENTS.LOAD_PRESENTATION_COURSE_SECTION, this.loadPresentationSection);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.sendLayoutEvent);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_STATE_VIDEO, this.changeStateVideo);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_RESIZE, this.changeResize);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_PDF_ZOOM, this.changePdfZoom);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_CARD, this.changeCard);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_TRANSPARENT_WHITEBOARD, this.sendTransparentWhiteboard);
    this.$edEventBus.$on(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.RESPONSE_USER_EVENT, this.processUserEvent);
    this.$edEventBus.$on(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS, this.loadPresentationElements);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_MULTIMEDIA_STATE, this.changeMultimediaState);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_SHARE_TASK, this.sendShareTask);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_TEXT_CHANGED, this.sendTextChanged);
    this.$edEventBus.$on(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.SEND_PENDING_MESSAGES, this.sendPendingMessages);
    this.$edEventBus.$on(FORWARD_PRESENTATION_MESSAGES_EVENT, this.forwardMessages);
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_SLIDE, this.changeSlideData);
    window.addEventListener(MESSAGE_EVENT, this.transparencyEvents, false);
    this.$Meetings.Store.obtainMeetingUsers(this.roomId);
  }

  beforeDestroy() {
    this._unload();
    this.$Courses.Store.setIsBoardSectionHomework(false);
    this.$store.commit(HOMEWORKS_STORE.MUTATIONS.CLEAR_PRESENTATION_SECTION_CONTENT);
    this.$store.commit(HOMEWORKS_STORE.MUTATIONS.UPDATE_SELETED_BOARD_INFO, {});
    this.$Meetings.Store.updateCurrentBoardUser({});
    this.$Meetings.Store.updateCurrentPresentationSection({});
    this.$Meetings.Store.removeCurrentMeetingUsers();
    this.$Meetings.Store.clearCurrentMeetingActiveUsers();
    this.$Meetings.Store.setShareTaskData({});
    this.$edEventBus.$off(COURSE_EVENTS.LOAD_PRESENTATION_COURSE_SECTION, this.loadPresentationSection);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.sendLayoutEvent);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_STATE_VIDEO, this.changeStateVideo);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_RESIZE, this.changeResize);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_PDF_ZOOM, this.changePdfZoom);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_CARD, this.changeCard);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_TRANSPARENT_WHITEBOARD, this.sendTransparentWhiteboard);
    this.$edEventBus.$off(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS, this.loadPresentationElements);
    this.$edEventBus.$off(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.RESPONSE_USER_EVENT, this.processUserEvent);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_MULTIMEDIA_STATE, this.changeMultimediaState);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_SHARE_TASK, this.sendShareTask);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_TEXT_CHANGED, this.sendTextChanged);
    this.$edEventBus.$off(PRESENTATION_VALUES.PRESENTATION_MODE_EVENTS.SEND_PENDING_MESSAGES, this.sendPendingMessages);
    this.$edEventBus.$off(FORWARD_PRESENTATION_MESSAGES_EVENT, this.forwardMessages);
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_SLIDE, this.changeSlideData);
    window.removeEventListener(MESSAGE_EVENT, this.transparencyEvents, false);
  }
}
